import React, { useEffect, useState } from 'react';
import { getPersonaStats, getPersonaDetail } from '../../services/admin/adminApi';
import GoogleAnalyticsButton from '../../components/admin/GoogleAnalyticsButton';
import PersonaDetail from '../../components/admin/PersonaDetail';

interface PersonaData {
  total_created: number;
  approved_count: number;
  rejected_count: number;
  approved_personas: Array<{
    name: string;
    description: string;
    persona_name?: string; // Kept for backward compatibility
    updated_at: string;
    in_conductor_config: boolean;
    chat_count: number;
  }>;
  rejected_personas: Array<{
    name: string;
    description: string;
    persona_name?: string; // Kept for backward compatibility
    updated_at: string;
    in_conductor_config: boolean;
    chat_count: number;
  }>;
  conductor_config_personas: string[];
  persona_usage_map: Record<string, number>;
}

interface PersonaDetail {
  name: string;
  description: string;
  completed_persona: string;
  research_report?: string;
  created_at: string;
  in_conductor_config: boolean;
  chat_count: number;
}

const Personas: React.FC = () => {
  const [data, setData] = useState<PersonaData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for persona detail view
  const [selectedPersonaName, setSelectedPersonaName] = useState<string | null>(null);
  const [personaDetail, setPersonaDetail] = useState<PersonaDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const personaData = await getPersonaStats();
        setData(personaData);
      } catch (err) {
        setError('Failed to load persona data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Set up refresh interval
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load persona detail when a persona is selected
  const loadPersonaDetail = async (personaName: string) => {
    setDetailLoading(true);
    setSelectedPersonaName(personaName);

    try {
      const detail = await getPersonaDetail(personaName);
      setPersonaDetail(detail);
    } catch (err) {
      console.error('Failed to load persona detail:', err);
      setError('Failed to load persona detail');
    } finally {
      setDetailLoading(false);
    }
  };

  // Return to list view
  const handleBack = () => {
    setSelectedPersonaName(null);
    setPersonaDetail(null);
    setError(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading persona data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 bg-red-100 rounded-md">
        <h3 className="text-lg font-semibold">Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 text-yellow-600 bg-yellow-100 rounded-md">
        No persona data available
      </div>
    );
  }

  // Calculate success rate
  const approvalRate = data.total_created > 0
    ? ((data.approved_count / data.total_created) * 100).toFixed(1)
    : '0.0';

  // Show detail view if a persona is selected
  if (selectedPersonaName) {
    return (
      <PersonaDetail
        detail={personaDetail}
        loading={detailLoading}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Persona Analytics</h2>
        <GoogleAnalyticsButton />
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Total Created</h3>
          <p className="mt-2 text-3xl font-bold">{data.total_created}</p>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Approved</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{data.approved_count}</p>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Rejected</h3>
          <p className="mt-2 text-3xl font-bold text-red-600">{data.rejected_count}</p>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Approval Rate</h3>
          <p className="mt-2 text-3xl font-bold">{approvalRate}%</p>
        </div>
      </div>

      {/* Persona lists */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Approved Personas */}
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="mb-4 text-lg font-semibold">Approved Personas</h3>
          
          {data.approved_personas.length > 0 ? (
            <div className="space-y-4">
              {data.approved_personas.map((persona, index) => (
                <div
                  key={index}
                  onClick={() => loadPersonaDetail(persona.name)}
                  className="p-4 border border-green-200 rounded-lg bg-green-50 cursor-pointer hover:bg-green-100 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start">
                    <h4 className="text-md font-semibold">{persona.name}</h4>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        persona.in_conductor_config
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      {persona.in_conductor_config ? '🟢 Available' : '🔵 DB Only'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{persona.description}</p>

                  {/* Chat count */}
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-700">
                      💬 {persona.chat_count} chat{persona.chat_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Show legacy persona_name if different from name */}
                  {persona.persona_name && persona.persona_name !== persona.name && (
                    <p className="mt-2 text-xs text-gray-500">
                      Legacy persona name: {persona.persona_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No approved personas found</p>
          )}
        </div>

        {/* Rejected Personas */}
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="mb-4 text-lg font-semibold">Rejected Personas</h3>
          
          {data.rejected_personas.length > 0 ? (
            <div className="space-y-4">
              {data.rejected_personas.map((persona, index) => (
                <div
                  key={index}
                  className="p-4 border border-red-200 rounded-lg bg-red-50"
                >
                  <div className="flex justify-between items-start">
                    <h4 className="text-md font-semibold">{persona.name}</h4>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        persona.in_conductor_config
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      {persona.in_conductor_config ? '🟢 Available' : '🔵 DB Only'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{persona.description}</p>

                  {/* Chat count */}
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-700">
                      💬 {persona.chat_count} chat{persona.chat_count !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Show legacy persona_name if different from name */}
                  {persona.persona_name && persona.persona_name !== persona.name && (
                    <p className="mt-2 text-xs text-gray-500">
                      Legacy persona name: {persona.persona_name}
                    </p>
                  )}

                  {/* Try to determine rejection reason */}
                  {persona.description.toLowerCase().includes('villain') ||
                   persona.description.toLowerCase().includes('evil') ||
                   persona.name.toLowerCase().includes('villain') ||
                   persona.name.toLowerCase().includes('evil') ? (
                    <p className="mt-2 text-xs font-medium text-red-600">
                      Likely rejected due to villain filter
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">
                      Rejection reason unknown
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No rejected personas found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Personas;
